import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { db } from "../../Firebase/firebaseConfig";
import {
  collection,
  addDoc,
  getDocs,
  orderBy,
  deleteDoc,
  doc,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";

// Types
export interface EventItem {
  id: string;
  title: string;
  booth: string;
  location: string;
  date: string;
  tag: string;
  createdAt?: string;
  updatedAt?: string;
}

interface EventState {
  events: EventItem[];
  loading: boolean;
  error: string | null;
}

// Initial state
const initialState: EventState = {
  events: [],
  loading: false,
  error: null,
};

//  Fetch Events
export const fetchEvents = createAsyncThunk<EventItem[]>(
  "eventList/fetchEvents",
  async (_, thunkAPI) => {
    try {
      const querySnapshot = await getDocs(collection(db, "events"));

      const data = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as EventItem[];

      return data;
    } catch (error: any) {
      console.log("Error fetching Firebase events data ======?:", error);
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);


//  Add Event
export const addEvent = createAsyncThunk<EventItem, Omit<EventItem, "id" | "createdAt" | "updatedAt">>(
  "eventList/addEvent",
  async (eventData) => {
    const docRef = await addDoc(collection(db, "events"), {
      ...eventData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    return {
      id: docRef.id,
      ...eventData,
      createdAt: new Date().toLocaleString(),
      updatedAt: new Date().toLocaleString(),
    };
  }
);

//  Delete Event
export const deleteEvent = createAsyncThunk<string, string>(
  "eventList/deleteEvent",
  async (eventId) => {
    await deleteDoc(doc(db, "events", eventId));
    return eventId;
  }
);

//  Update Event
export const updateEvent = createAsyncThunk<EventItem, EventItem>(
  "eventList/updateEvent",
  async (eventData) => {
    const { id, ...updateData } = eventData;
    await updateDoc(doc(db, "events", id), {
      ...updateData,
      updatedAt: serverTimestamp(),
    });

    return {
      ...eventData,
      updatedAt: new Date().toLocaleString(),
    };
  }
);

// SLICE
const eventListSlice = createSlice({
  name: "eventList",
  initialState,
  reducers: {
    // Add this to set events manually from a hook
    setEvents: (state, action: PayloadAction<EventItem[]>) => {
      state.events = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch
      .addCase(fetchEvents.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchEvents.fulfilled, (state, action: PayloadAction<EventItem[]>) => {
        state.loading = false;
        state.events = action.payload;
      })
      .addCase(fetchEvents.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || null;
      })

      // Add
      .addCase(addEvent.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addEvent.fulfilled, (state, action: PayloadAction<EventItem>) => {
        state.loading = false;
        state.events.unshift(action.payload);
      })
      .addCase(addEvent.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || null;
      })

      // Delete
      .addCase(deleteEvent.fulfilled, (state, action: PayloadAction<string>) => {
        state.events = state.events.filter((event) => event.id !== action.payload);
      })

      // Update
      .addCase(updateEvent.fulfilled, (state, action: PayloadAction<EventItem>) => {
        const index = state.events.findIndex((event) => event.id === action.payload.id);
        if (index !== -1) {
          state.events[index] = action.payload;
        }
      });
  },
});

// Export actions and reducer
export const { setEvents } = eventListSlice.actions;
export default eventListSlice.reducer;
